// Capacitor-aware bridge layer. This module is import-safe in the browser:
// every plugin import is lazy + dynamic so the web bundle doesn't try to
// load native code. When running inside a Capacitor WebView (real device,
// or the simulator), the helpers below activate; otherwise everything
// no-ops cleanly.
//
// Why this lives in its own module instead of inlined in main.jsx:
//  - keeps Capacitor a single dependency edge so we can rip it out if we
//    decide to drop the native shell.
//  - lets unit tests of the rest of the app mock it via vi.mock("./native").
//  - the lazy import boundary means a web-only deploy still ships the
//    Capacitor SDK code-split off the critical path.

let _cachedNative = null;
export function isNative() {
  if (_cachedNative !== null) return _cachedNative;
  try {
    // Capacitor v8 exposes `window.Capacitor.isNativePlatform()` synchronously
    // once the runtime bootstraps. It's set before any JS we control runs,
    // so reading it here is reliable. Fall back to user-agent sniff if the
    // global isn't present (older Capacitor or custom WebView).
    if (typeof window !== "undefined") {
      const C = window.Capacitor;
      if (C && typeof C.isNativePlatform === "function") {
        _cachedNative = !!C.isNativePlatform();
        return _cachedNative;
      }
      if (/CapacitorJS/i.test(navigator.userAgent || "")) {
        _cachedNative = true;
        return _cachedNative;
      }
    }
  } catch { /* ignore */ }
  _cachedNative = false;
  return _cachedNative;
}

export function platform() {
  try { return window.Capacitor?.getPlatform?.() || "web"; } catch { return "web"; }
}

// Initialize native-only side effects: status bar style, splash hide,
// keyboard behavior, app lifecycle hooks. Safe to call on web — it
// returns early.
export async function initNative() {
  if (!isNative()) return;

  // CSS marker so the index.css safe-area rules activate. Class is set
  // on <html> rather than <body> because Tailwind's media-query reset
  // touches body and we want our rules to win.
  try { document.documentElement.classList.add("capacitor-app"); } catch { /* ignore */ }

  // Persistent native session — was previously signed out on every cold
  // start (shared-iPad terminal pattern). That broke Google OAuth: the
  // post-OAuth `window.location.replace("/")` triggered initNative which
  // wiped the freshly-set session before the user ever saw the dashboard.
  // Customers/owners are now kept signed in across launches; explicit
  // logout button still works.
  try {
    try {
      localStorage.removeItem("petos:oauth-pending");
      localStorage.removeItem("petos:booking-redirect");
    } catch { /* ignore */ }
    try {
      if (window.location.hash) window.history.replaceState(null, "", window.location.pathname);
    } catch { /* ignore */ }
  } catch { /* ignore */ }

  // Splash screen — hide as soon as React has rendered. The HTML loader
  // in index.html is removed by main.jsx; the native splash needs an
  // explicit hide call or it sits over the WebView for ~1.5s.
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    // Small delay so the WebView has actually painted; without this the
    // user briefly sees a blank state between splash and first render.
    setTimeout(() => SplashScreen.hide().catch(() => {}), 300);
  } catch { /* plugin missing — ignore */ }

  // Status bar — match the app's pink header. iOS reads `style:DARK` as
  // "dark content on a light background" which is what we want against
  // the white app background.
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
    if (platform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#FFD8E8" }).catch(() => {});
    }
  } catch { /* ignore */ }

  // Keyboard — let the WebView resize so inputs aren't covered. Capacitor
  // emits keyboardWillShow / keyboardWillHide events that we can hook if
  // we ever need to tweak focus scrolling, but the default `resize:native`
  // handles 95% of cases.
  // Keyboard plugin import skipped on purpose. `setAccessoryBarVisible` was
  // previously called to hide the iOS QuickType bar, but on iPad it
  // intermittently interferes with the keyboard appearing at all on first
  // focus. The default WKWebView keyboard behavior is what we want.

  // App lifecycle — wire to the visibility helper used by useAppState so
  // backgrounded sync + auth refresh happen in the right order. Without
  // this, the WebView resumes mid-suspended-state and triggers the same
  // hang we see on web.
  try {
    const { App } = await import("@capacitor/app");
    App.addListener("appStateChange", (state) => {
      try {
        if (typeof window !== "undefined" && typeof window.__petosOnAppState === "function") {
          window.__petosOnAppState(state.isActive ? "active" : "background");
        }
      } catch { /* ignore */ }
    });
    // Back-button on Android pops the hash route, falls back to native
    // exit if there's no history. iOS doesn't fire this event.
    App.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) { window.history.back(); }
      else { App.exitApp().catch(() => {}); }
    });
    // OAuth callback handler. signInWithGoogle (in src/lib/auth.js) opens
    // the Google login in the in-app Browser, redirecting to
    // com.pettobook.app://login-callback#access_token=...&refresh_token=...
    // The OS routes that URL back into the app and Capacitor fires
    // appUrlOpen. We extract the tokens, hand them to Supabase, then close
    // the in-app browser so the user lands back in the app authenticated.
    App.addListener("appUrlOpen", async (event) => {
      try {
        const url = event?.url || "";
        // Accept both the legacy custom-scheme callback and the new
        // Universal Link callback (https://pettobook.com/auth/native-callback)
        // — iOS routes the latter directly to the app when the AASA file
        // + associated-domains entitlement are in place, removing the
        // need for the relay HTML and user tap.
        const isCustomScheme = url.startsWith("com.pettobook.app://");
        const isUniversalLink = url.startsWith("https://pettobook.com/auth/callback");
        if (!isCustomScheme && !isUniversalLink) return;
        // Tokens may arrive in either the hash or the query string
        // depending on Supabase's PKCE vs implicit flow. Parse both.
        const after = url.split("//").slice(1).join("//");
        const tail = after.includes("#") ? after.split("#").slice(1).join("#")
                   : after.includes("?") ? after.split("?").slice(1).join("?")
                   : "";
        const params = new URLSearchParams(tail);
        const accessToken = params.get("access_token");
        const refreshToken = params.get("refresh_token");
        const code = params.get("code");
        const { supabase } = await import("./supabaseClient.js");
        if (accessToken && refreshToken) {
          await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
        } else if (code) {
          // PKCE flow — exchange the auth code for a session.
          await supabase.auth.exchangeCodeForSession(code);
        }
        // Close the in-app browser so the user sees the app.
        try {
          const { Browser } = await import("@capacitor/browser");
          await Browser.close();
        } catch { /* plugin missing — ignore */ }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[PettoBook] appUrlOpen handler failed:", e);
      }
    });
  } catch { /* ignore */ }
}

// Push notification setup. Caller passes a callback that registers the
// device token with our backend (so we can target this device with APNs
// from the create-customer-account / booking-confirmed flows). Permission
// flow is initiated on demand — don't call on cold start; call after
// login so the prompt has context.
export async function registerForPush(onToken, onEvent) {
  const emit = (stage, detail) => { try { onEvent?.(stage, detail == null ? "" : String(detail)); } catch { /* ignore */ } };
  if (!isNative()) { emit("skip", "not-native"); return; }
  let PushNotifications;
  try {
    const mod = await import("@capacitor/push-notifications");
    PushNotifications = mod.PushNotifications;
    if (!PushNotifications) { emit("import-fail", "no PushNotifications export"); return; }
  } catch (e) { emit("import-fail", e?.message || String(e)); return; }
  try {
    let perm;
    try { perm = await PushNotifications.checkPermissions(); emit("perm-check", perm?.receive); }
    catch (e) { emit("check-fail", e?.message || String(e)); }
    if (!perm || (perm.receive !== "granted" && perm.receive !== "denied")) {
      try { perm = await PushNotifications.requestPermissions(); emit("perm-req", perm?.receive); }
      catch (e) { emit("req-fail", e?.message || String(e)); return; }
    }
    if (perm.receive !== "granted") { emit("perm-denied", perm.receive); return; }
    PushNotifications.addListener("registration", (token) => {
      emit("token", (token?.value || "").slice(0, 10));
      try { onToken?.(token.value); } catch { /* ignore */ }
    });
    PushNotifications.addListener("registrationError", (err) => {
      // eslint-disable-next-line no-console
      console.warn("[PettoBook] push registration error:", err);
      emit("reg-error", err?.error || (typeof err === "string" ? err : JSON.stringify(err)));
    });
    // Surface incoming notifications to the existing toast/notification
    // system. The DB trigger that fans out web push will also fan to APNs
    // once the device token is registered server-side.
    PushNotifications.addListener("pushNotificationReceived", (n) => {
      try {
        if (typeof window !== "undefined" && typeof window.__petosToast === "function") {
          window.__petosToast(n.title || n.body || "新通知");
        }
      } catch { /* ignore */ }
    });
    PushNotifications.addListener("pushNotificationActionPerformed", (n) => {
      try {
        if (typeof window !== "undefined" && typeof window.__petosOnPushTap === "function") {
          window.__petosOnPushTap(n.notification);
        }
      } catch { /* ignore */ }
    });
    try { await PushNotifications.register(); emit("register-called", ""); }
    catch (e) { emit("register-fail", e?.message || String(e)); }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[PettoBook] registerForPush failed:", e);
    emit("exception", e?.message || String(e));
  }
}
